
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LogLevelType} from './LogLevelType';
import {FieldModelLogLevelType} from './LogLevelType';

/**
 * JournalRecord struct
 */
class JournalRecord {
  /**
   * Initialize struct
   * @param {Date=} time
   * @param {LogLevelType=} logLevel
   * @param {!string=} message
   * @constructor
   */
  constructor (time = null, logLevel = null, message = '') {
    this.Time = time
    this.LogLevel = logLevel
    this.Message = message
  }

  /**
   * Copy struct (shallow copy)
   * @this {!JournalRecord}
   * @param {!JournalRecord} other Other struct
   * @returns {!JournalRecord} This struct
   */
  copy (other) {
    if (other.Time != null) {
      if (other.Time instanceof Date) {
        this.Time = new Date(other.Time.getTime())
      } else {
        this.Time = new Date(Math.round(other.Time / 1000000))
      }
    } else {
      this.Time = null
    }
    if (other.LogLevel != null) {
      this.LogLevel = LogLevelType.fromObject(other.LogLevel)
    } else {
      this.LogLevel = null
    }
    if (other.Message != null) {
      this.Message = other.Message
    } else {
      this.Message = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!JournalRecord}
   * @returns {!JournalRecord} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new JournalRecordModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new JournalRecordModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!JournalRecord}
   * @param {!JournalRecord} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof JournalRecord)) {
      throw new TypeError('Instance of JournalRecord is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!JournalRecord}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Time: ((this.Time != null) ? (this.Time.getTime() * 1000000) : null),
      LogLevel: ((this.LogLevel != null) ? this.LogLevel : null),
      Message: ((this.Message != null) ? this.Message : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return JournalRecord.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!JournalRecord} other Object value
   * @returns {!JournalRecord} Created struct
   */
  static fromObject (other) {
    return new JournalRecord().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!JournalRecord}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return JournalRecord.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!JournalRecord}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 98
  }
}

export { JournalRecord };

/**
 * Fast Binary Encoding JournalRecord field model
 */
class FieldModelJournalRecord extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Time = new fbe.FieldModelOptional(new fbe.FieldModelTimestamp(buffer, 4 + 4), buffer, 4 + 4)
    this.LogLevel = new fbe.FieldModelOptional(new FieldModelLogLevelType(buffer, this.Time.fbeOffset + this.Time.fbeSize), buffer, this.Time.fbeOffset + this.Time.fbeSize)
    this.Message = new fbe.FieldModelString(buffer, this.LogLevel.fbeOffset + this.LogLevel.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Time.fbeSize + this.LogLevel.fbeSize + this.Message.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Time.fbeExtra + this.LogLevel.fbeExtra + this.Message.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelJournalRecord.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 98
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelJournalRecord}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelJournalRecord}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Time.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LogLevel.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize

    if ((fbeCurrentSize + this.Message.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Message.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelJournalRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelJournalRecord}
   * @param {!JournalRecord} fbeValue Default value, defaults is new JournalRecord()
   * @returns {!JournalRecord} JournalRecord value
   */
  get (fbeValue = new JournalRecord()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelJournalRecord}
   * @param {!JournalRecord} fbeValue JournalRecord value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Time.fbeSize) <= fbeStructSize) {
      fbeValue.Time = this.Time.get()
    } else {
      fbeValue.Time = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Time.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) <= fbeStructSize) {
      fbeValue.LogLevel = this.LogLevel.get()
    } else {
      fbeValue.LogLevel = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize

    if ((fbeCurrentSize + this.Message.fbeSize) <= fbeStructSize) {
      fbeValue.Message = this.Message.get()
    } else {
      fbeValue.Message = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelJournalRecord}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelJournalRecord}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelJournalRecord}
   * @param {!JournalRecord} fbeValue JournalRecord value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelJournalRecord}
   * @param {JournalRecord} fbeValue JournalRecord value
   */
  setFields (fbeValue) {
    this.Time.set(fbeValue.Time)
    this.LogLevel.set(fbeValue.LogLevel)
    this.Message.set(fbeValue.Message)
  }
}

export { FieldModelJournalRecord };

/**
 * Fast Binary Encoding JournalRecord model
 */
class JournalRecordModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelJournalRecord(this.buffer, 4)
  }

  /**
   * Get the JournalRecord model
   * @this {!JournalRecordModel}
   * @returns {!FieldModelJournalRecord} model JournalRecord model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!JournalRecordModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!JournalRecordModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return JournalRecordModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!JournalRecordModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelJournalRecord.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!JournalRecordModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!JournalRecordModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!JournalRecordModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!JournalRecordModel}
   * @param {!JournalRecord} value JournalRecord value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!JournalRecordModel}
   * @param {!JournalRecord} value JournalRecord value, defaults is new JournalRecord()
   * @return {!object} Deserialized JournalRecord value and its size
   */
  deserialize (value = new JournalRecord()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new JournalRecord(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new JournalRecord(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!JournalRecordModel}
   * @param {!number} prev Previous JournalRecord model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { JournalRecordModel };
